/**
 * Skeleton Component - Enhanced loading placeholders with more variants
 */

import React from 'react'

const Skeleton = ({
  variant = 'text',
  className = '',
  count = 1,
  width = '100%',
  height = 'auto',
  rounded = 'md',
  animate = true,
  shimmer = true,
  ...props
}) => {
  const variants = {
    text: 'h-4 w-full',
    heading: 'h-8 w-2/3',
    title: 'h-6 w-3/4',
    subtitle: 'h-5 w-1/2',
    avatar: 'h-12 w-12 rounded-full',
    card: 'h-48 w-full rounded-xl',
    image: 'h-64 w-full rounded-xl',
    button: 'h-10 w-32 rounded-lg',
    badge: 'h-6 w-16 rounded-full',
    table: 'h-4 w-full rounded',
    form: 'h-12 w-full rounded-lg',
    chart: 'h-40 w-full rounded-xl',
    progress: 'h-2 w-full rounded-full',
    icon: 'h-8 w-8 rounded-full',
    thumbnail: 'h-20 w-20 rounded-lg',
    'card-sm': 'h-32 w-full rounded-xl',
    'card-lg': 'h-64 w-full rounded-xl',
  }

  const roundedClasses = {
    none: '',
    sm: 'rounded-sm',
    md: 'rounded-md',
    lg: 'rounded-lg',
    xl: 'rounded-xl',
    full: 'rounded-full',
  }

  const shimmerClass = animate && shimmer ? 'shimmer' : ''
  const pulseClass = animate && !shimmer ? 'animate-pulse' : ''

  const baseStyles = `
    bg-neutral-100
    ${shimmerClass}
    ${pulseClass}
    ${roundedClasses[rounded] || roundedClasses.md}
    ${variants[variant] || variants.text}
    ${className}
  `

  const style = {
    width: width,
    height: height,
    ...props.style,
  }

  if (count > 1) {
    return (
      <div className="space-y-3">
        {Array.from({ length: count }).map((_, index) => (
          <div key={index} className={baseStyles} style={style} {...props} />
        ))}
      </div>
    )
  }

  return <div className={baseStyles} style={style} {...props} />
}

// Preset skeleton components for common use cases
export const SkeletonText = (props) => <Skeleton variant="text" {...props} />
export const SkeletonHeading = (props) => <Skeleton variant="heading" {...props} />
export const SkeletonTitle = (props) => <Skeleton variant="title" {...props} />
export const SkeletonAvatar = (props) => <Skeleton variant="avatar" {...props} />
export const SkeletonCard = (props) => <Skeleton variant="card" {...props} />
export const SkeletonImage = (props) => <Skeleton variant="image" {...props} />
export const SkeletonButton = (props) => <Skeleton variant="button" {...props} />
export const SkeletonBadge = (props) => <Skeleton variant="badge" {...props} />
export const SkeletonTable = (props) => <Skeleton variant="table" {...props} />

// Compound skeleton for tables with headers
export const SkeletonTableRows = ({ rows = 5, cols = 4, className = '' }) => (
  <div className={`space-y-3 ${className}`}>
    {/* Header */}
    <div className="flex gap-4">
      {Array.from({ length: cols }).map((_, i) => (
        <div key={i} className="h-4 w-full bg-neutral-100 rounded shimmer" />
      ))}
    </div>
    {/* Rows */}
    {Array.from({ length: rows }).map((_, i) => (
      <div key={i} className="flex gap-4">
        {Array.from({ length: cols }).map((_, j) => (
          <div key={j} className="h-4 w-full bg-neutral-100 rounded shimmer" />
        ))}
      </div>
    ))}
  </div>
)

// Compound skeleton for cards grid
export const SkeletonCardGrid = ({ count = 6, className = '' }) => (
  <div className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 ${className}`}>
    {Array.from({ length: count }).map((_, i) => (
      <div key={i} className="bg-surface border border-neutral-200 rounded-xl p-6 space-y-4">
        <div className="flex items-start justify-between">
          <div className="space-y-2">
            <div className="h-4 w-32 bg-neutral-100 rounded shimmer" />
            <div className="h-3 w-24 bg-neutral-100 rounded shimmer" />
          </div>
          <div className="h-6 w-12 bg-neutral-100 rounded-full shimmer" />
        </div>
        <div className="h-3 w-full bg-neutral-100 rounded shimmer" />
        <div className="h-3 w-2/3 bg-neutral-100 rounded shimmer" />
        <div className="flex items-center justify-between mt-3">
          <div className="h-3 w-20 bg-neutral-100 rounded shimmer" />
          <div className="h-8 w-32 bg-neutral-100 rounded shimmer" />
        </div>
      </div>
    ))}
  </div>
)

// Compound skeleton for KPIs
export const SkeletonKpiRow = ({ count = 4, className = '' }) => (
  <div className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 ${className}`}>
    {Array.from({ length: count }).map((_, i) => (
      <div key={i} className="surface p-5 space-y-3">
        <div className="flex items-center justify-between">
          <div className="w-9 h-9 bg-neutral-100 rounded shimmer" />
          <div className="h-3 w-8 bg-neutral-100 rounded shimmer" />
        </div>
        <div className="h-6 w-20 bg-neutral-100 rounded shimmer" />
        <div className="h-3 w-16 bg-neutral-100 rounded shimmer" />
      </div>
    ))}
  </div>
)

// Profile skeleton
export const SkeletonProfile = ({ className = '' }) => (
  <div className={`flex items-center gap-4 ${className}`}>
    <div className="w-16 h-16 bg-neutral-100 rounded-full shimmer" />
    <div className="space-y-2 flex-1">
      <div className="h-5 w-40 bg-neutral-100 rounded shimmer" />
      <div className="h-3 w-24 bg-neutral-100 rounded shimmer" />
    </div>
  </div>
)

// Dashboard skeleton
export const SkeletonDashboard = ({ className = '' }) => (
  <div className={`space-y-6 ${className}`}>
    <div className="h-8 w-48 bg-neutral-100 rounded shimmer" />
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={i} className="h-24 bg-neutral-100 rounded-xl shimmer" />
      ))}
    </div>
    <div className="h-64 bg-neutral-100 rounded-xl shimmer" />
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2 h-48 bg-neutral-100 rounded-xl shimmer" />
      <div className="h-48 bg-neutral-100 rounded-xl shimmer" />
    </div>
  </div>
)

Skeleton.displayName = 'Skeleton'

export default Skeleton